import React, { useMemo, useState, useRef } from 'react';
import { Select } from 'antd';
import { CsSelect } from 'components/antd-extension/CsSelect';
import { QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import { ze } from 'utils/zhEn';
import { isNotNullish } from 'utils/nullish';
import RestHelper from 'utils/RestHelper';

// 缓存：key为 `${appId}_${fieldName}`，value为选项列表
const optionsCache = new Map();
// 记录上次使用的appId，用于在appId变化时清空缓存
let lastAppId = null;

const IcfGameMatrixFieldSelect = ({
  appId,
  fieldName,
  isReadOnly,
  fieldValue,
  onChangeFieldValue,
}) => {
  const SearchMethod = useMemo(() => ({
    IN: 'IN',
    NOT_IN: 'NOT_IN',
  }), []);

  const SearchMethodToInfo = useMemo(() => ({
    [SearchMethod.IN]: {
      queryType: QueryType.TERMS,
      not: false,
    },
    [SearchMethod.NOT_IN]: {
      queryType: QueryType.TERMS,
      not: true,
    },
  }), [SearchMethod.IN, SearchMethod.NOT_IN]);

  const QUERY_TYPE_OPTIONS = useMemo(() => [
    { name: ze('包含', 'Contains'), value: SearchMethod.IN },
    { name: ze('不包含', 'Not Contains'), value: SearchMethod.NOT_IN },
  ].map(x => ({
    ...x,
    label: x.name,
  })), [SearchMethod.IN, SearchMethod.NOT_IN]);

  const findSearchMethod = (queryType, not) => {
    const SearchMethodInfoEntries = Object.entries(SearchMethodToInfo);
    const foundEntry = SearchMethodInfoEntries.find(([k, v]) => {
      return v.queryType === queryType && v.not === Boolean(not);
    });
    return isNotNullish(foundEntry) ? foundEntry[0] : null;
  };

  const {
    queryType: nullableQueryType,
    not,
    terms,
  } = fieldValue || {};

  const queryType = nullableQueryType || QueryType.TERMS;
  let searchMethodValue = findSearchMethod(queryType, not);
  if (!searchMethodValue) {
    searchMethodValue = SearchMethod.IN;
  }

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  // 检查appId是否变化，如果变化则清空缓存
  if (lastAppId !== null && lastAppId !== appId) {
    optionsCache.clear();
    fetchedRef.current = false;
  }
  lastAppId = appId;

  const cacheKey = `${appId}_${fieldName}`;

  const fetchOptions = async () => {
    // 如果已经有缓存，直接使用
    if (optionsCache.has(cacheKey)) {
      setOptions(optionsCache.get(cacheKey));
      return;
    }

    // 如果正在加载或已经获取过，跳过
    if (loading || fetchedRef.current) {
      return;
    }

    setLoading(true);
    fetchedRef.current = true;

    try {
      const rsp = await RestHelper.post('/api/issue/queryIssueAggregate', {
        appId,
        customFields: [
          {
            isSelfDefinedKeyValue: true,
            name: `K#${fieldName}`,
            aggregateType: 0,
            termsSizeLimit: 1000,
          },
        ],
        searchConditionGroup: {
          conditions: [
            {
              field: 'crashUploadTime',
              queryType: 'RANGE_RELATIVE_DATETIME',
              gte: 604800000,
            },
          ],
        },
      });

      const bucketList = rsp.json.data?.[0]?.bucketList || [];
      const fetchedOptions = bucketList.map(item => ({
        label: item.key,
        value: item.key,
      }));

      // 缓存选项
      optionsCache.set(cacheKey, fetchedOptions);
      setOptions(fetchedOptions);
    } catch (e) {
      console.error('Failed to fetch game matrix field options:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownVisibleChange = (open) => {
    if (open) {
      fetchOptions();
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <Select
        style={{ width: '120px', flex: '0 0 auto' }}
        options={QUERY_TYPE_OPTIONS}
        optionFilterProp='name'
        optionLabelProp='name'
        value={searchMethodValue}
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          const newInfo = SearchMethodToInfo[v];
          onChangeFieldValue({ queryType: newInfo.queryType, not: newInfo.not, terms });
        }}
      />
      <CsSelect
        style={{ width: '100%', fontSize: '14px' }}
        mode='multiple'
        allowClear
        showSearch
        optionFilterProp='label'
        options={options}
        loading={loading}
        placeholder={ze('请选择', 'Please select')}
        value={terms}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onChange={(v) => {
          if (isReadOnly) {
            return;
          }
          onChangeFieldValue({ queryType, not, terms: v });
        }}
      />
    </div>
  );
};

export default IcfGameMatrixFieldSelect;
